import type { ReactNode } from 'react';
import { CheckCircle2, ShieldAlert, ShieldCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  isBooked,
  type ReconciliationStateResponse,
  type FundTransactionStatus,
} from '@/types/fund-transactions';

export type FieldSpan = 'ONE_COLUMN' | 'TWO_COLUMNS' | 'FULL_ROW';

const FIELD_SPAN_CLASS: Record<FieldSpan, string | undefined> = {
  ONE_COLUMN: undefined,
  TWO_COLUMNS: 'col-span-2',
  FULL_ROW: 'col-span-2 sm:col-span-3',
};

export function Field({
  label,
  children,
  span = 'ONE_COLUMN',
}: {
  label: string;
  children: ReactNode;
  span?: FieldSpan;
}) {
  return (
    <div className={FIELD_SPAN_CLASS[span]}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm whitespace-pre-wrap">{children}</dd>
    </div>
  );
}

export function VerificationStatusBadge({
  status,
  reconciliation,
}: {
  status: FundTransactionStatus;
  reconciliation: ReconciliationStateResponse | undefined;
}) {
  if (!reconciliation) return null;

  const isPastDraft = isBooked(status);

  if (isPastDraft) {
    if (reconciliation.manual_approval !== null) {
      return (
        <Badge
          variant="secondary"
          className="gap-1 font-normal border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300"
        >
          <ShieldAlert className="h-3 w-3" />
          Manual override
        </Badge>
      );
    }
    return (
      <Badge
        variant="secondary"
        className="gap-1 font-normal border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
      >
        <ShieldCheck className="h-3 w-3" />
        Verified
      </Badge>
    );
  }

  if (reconciliation.threshold <= 0) return null;

  const isReady =
    reconciliation.verified_source_count >= reconciliation.threshold;

  return (
    <Badge
      variant="secondary"
      className={
        isReady
          ? 'gap-1 font-normal border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'gap-1 font-normal border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300'
      }
    >
      <CheckCircle2 className="h-3 w-3" />
      {reconciliation.verified_source_count} of {reconciliation.threshold}{' '}
      verified
    </Badge>
  );
}
